import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Pagination,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BarragemData, useUserContext } from "../../context";
import { Paginated, RequestError, useApi } from "../../services";
import { ErrorOnDialog, GISTMCheckItem, GISTMCheckItemButton, GISTMRequisitoButton, GISTMRequisitoItem, LoadingModal } from "../../components";
import { CompanyData } from "../homePage";
import { GISTMChecklist, GISTMRequisitos } from "../../services/api/gistm";
import { objectToHash } from "../../core";
import { OpenInNew } from "@mui/icons-material";

interface CompanyWithDam {
  company: CompanyData;
  dams: BarragemData[];
}

const initialData = {
  page: 1,
  items: [],
  perPage: 50,
  totalItems: 0,
  totalPages: 1,
};

export const HBRGISTM: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const hbrUser = useUserContext();

  const timeout = useRef<NodeJS.Timeout>();
  const [state, setState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RequestError>();
  const [companies, setCompanies] = useState<CompanyWithDam[]>([]);
  const [showing, setShowing] = useState<"checklist" | "requisitos">();
  const [selectedBarragem, setSelectedBarragem] = useState<BarragemData>();
  const [data, setData] = useState<Paginated<GISTMChecklist | GISTMRequisitos>>(initialData);

  useEffect(() => {
    if (!hbrUser || "error" in hbrUser) {
      return;
    }

    setLoading(true);

    api.company.getAllNotPaginated().then(async (data) => {
      if ("error" in data) {
        setError(data);
        setLoading(false);
        return;
      }

      const companiesWithDams: CompanyWithDam[] = [];

      for (const company of data) {
        if (company.barragens.length === 0) continue;

        companiesWithDams.push({ company, dams: [] });
        for (const id of company.barragens) {
          const dam = await api.barragem.getBarragemById(id);
          if ("error" in dam) {
            setError(dam);
            setLoading(false);
            return;
          }
          companiesWithDams[companiesWithDams.length - 1].dams.push(dam);
        }
      }

      setCompanies(companiesWithDams);
      setLoading(false);
    });
  }, [hbrUser, navigate, setCompanies]);

  // Obtendo items do checklist
  useEffect(() => {
    if (!selectedBarragem || !showing) return;

    setLoading(true);

    if (showing === "checklist") {
      api.gistm.getAllChecklist(selectedBarragem.id, data.page, data.perPage).then(val => {
        if ("error" in val) {
          setLoading(false);
          setError(val);
          return;
        }
        setData(val);
        setLoading(false);
      });
    } else if (showing === "requisitos") {
      api.gistm.getAllRequisitos(selectedBarragem.id, data.page, data.perPage).then(val => {
        if ("error" in val) {
          setLoading(false);
          setError(val);
          return;
        }
        setData(val);
        setLoading(false);
      });

    }

  }, [selectedBarragem, showing, state, data.page, data.perPage]);

  const updateUrlRepositorio = (barragemId: number, url: string) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    setSelectedBarragem(prev => prev ? { ...prev, urlRepositorioGISTM: url } : prev);

    timeout.current = setTimeout(async () => {
      const response = await api.barragem.updateUrlRepositorioGISTM(barragemId, url);
      if ('error' in response) {
        setError(response);
        return;
      }
      setSelectedBarragem(response);
      setCompanies(prevCompanies => {
        return prevCompanies.map(companyWithDams => {
          return {
            ...companyWithDams,
            dams: companyWithDams.dams.map(dam => {
              if (dam.id === barragemId) {
                return response;
              }
              return dam;
            }),
          };
        });
      });
    }, 500);
  };

  return (
    <>
      {error && <ErrorOnDialog
        open={Boolean(error)}
        error={error.error}
        message={error.message}
        onClose={() => setError(undefined)}
      />}

      {loading && < LoadingModal open={loading} />}

      <Box sx={{ height: "100%", display: "flex", flexDirection: "row", p: "5px", gap: 2 }}>
        {/* Empreendimentos e Barragens */}
        <Box sx={{ height: "100%", flex: 1, display: "flex", flexDirection: "column", gap: "5px", overflowY: "auto" }}>
          {companies.map((companyWithDams) => (
            <Box key={companyWithDams.company.id} sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>

              <Typography fontSize={12} fontWeight="bold" color="text.primary">
                {companyWithDams.company.nome}:
              </Typography>

              {
                companyWithDams.dams.map((barragem) => (
                  <Paper
                    key={barragem.id}
                    elevation={selectedBarragem?.id === barragem.id ? 3 : 0}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: "5px",
                      gap: "5px",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      cursor: "pointer",
                      // backgroundColor: selectedBarragem?.id === barragem.id ? "primary.main" : "background.paper",
                      // color: selectedBarragem?.id === barragem.id ? "primary.contrastText" : "text.primary",
                    }}
                    onClick={() => {
                      setShowing(undefined);
                      setData(initialData);
                      setSelectedBarragem(barragem);
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography fontSize={12} fontWeight="bold">
                        {barragem.nome}
                      </Typography>

                      <Tooltip title='Ir para a barragem'>
                        <IconButton size="small" onClick={() => navigate(`/app/GISTM/${barragem.id}`)}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {selectedBarragem?.id === barragem.id &&
                      <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label="URL para acesso ao Repositório"
                        value={selectedBarragem.urlRepositorioGISTM || ""}
                        onChange={(e) => updateUrlRepositorio(selectedBarragem.id, e.target.value)}
                      />
                    }

                    {selectedBarragem?.id === barragem.id &&
                      <Box sx={{
                        gap: "5px",
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "end"
                      }}>
                        <Button
                          variant={showing === "requisitos" ? "contained" : "outlined"}
                          color="secondary"
                          size="small"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            setShowing("requisitos");
                            setData(initialData);
                          }}
                        >
                          Requisitos
                        </Button>
                        <Button
                          variant={showing === "checklist" ? "contained" : "outlined"}
                          color="secondary"
                          size="small"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            setShowing("checklist");
                            setData(initialData);
                          }}
                        >
                          Checklist
                        </Button>
                      </Box>
                    }
                  </Paper>
                ))
              }

              <Divider />

            </Box>
          ))}
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ height: "100%", width: "100%", flex: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: "15px" }}>
          {selectedBarragem && showing === "checklist" && <GISTMCheckItemButton
            barragem={selectedBarragem.id}
            onFinish={() => setState(prev => !prev)}
            title="Adicionar Novo Item +"
          />}
          {selectedBarragem && showing === "requisitos" && <GISTMRequisitoButton
            barragem={selectedBarragem.id}
            onFinish={() => setState(prev => !prev)}
            title="Adicionar Novo Requisito +"
          />}


          <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", gap: "10px", overflowY: "auto" }}>
            {showing === "checklist" && (data.items as GISTMChecklist[]).map(item => {
              return <GISTMCheckItem
                editable
                checkData={item}
                key={objectToHash(item)}
                onEdit={() => setState(prev => !prev)}
                onRemove={() => setState(prev => !prev)}
              />;
            })}

            {showing === "requisitos" && (data.items as GISTMRequisitos[]).map(item => {
              return <GISTMRequisitoItem
                editable
                initRequisito={item}
                key={objectToHash(item)}
                onEdit={() => setState(prev => !prev)}
                onRemove={() => setState(prev => !prev)}
              />;
            })}

          </Box>

          {/* Paginação */}
          <Pagination
            showFirstButton showLastButton
            size="small"
            page={data.page}
            count={data.totalPages}
            onChange={(_, value) => setData({ ...data, page: value })}
          />

        </Box>

      </Box>
    </>
  );
};
